import {announce} from '@github-ui/aria-live'
import {Filter} from '@github-ui/filter'
import {repoAttestationsIndexPath} from '@github-ui/paths'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {useClientValue} from '@github-ui/use-client-value'
import {useCSRFToken} from '@github-ui/use-csrf-token'
import {useSearchParams} from '@github-ui/use-navigate'
import {Heading, Link, PageLayout} from '@primer/react'
import {Pagehead} from '@primer/react/deprecated'
import {useEffect, useMemo, useRef, useState} from 'react'

import {AttestationsList} from '../components/AttestationsList'
import {useAttestationsResults} from '../hooks/use-attestations-results'
import {type AttestationsPayload, SortOrder, type SortOrderType} from '../types'
import {CreatedFilterProvider, PredicateTypeFilterProvider} from '../utils/filter-utils'
import styles from './AttestationsIndex.module.css'

export const AttestationsIndex = () => {
  const initialPayload = useRoutePayload<AttestationsPayload>()
  const {repo} = initialPayload
  const [params] = useSearchParams()
  const csrfToken = useCSRFToken(repoAttestationsIndexPath({repo}), 'delete') || ''

  const {currentPayload, currentQuery, fetchResults, deleteAttestations, changePage} = useAttestationsResults(
    initialPayload,
    csrfToken,
  )
  const {attestations, pageInfo, totalCount, feedbackUrl} = currentPayload

  // Announce results to screen readers when the search/filter query or data changes
  const lastQueryRef = useRef<string>('')
  useEffect(() => {
    if (totalCount !== undefined && currentQuery !== lastQueryRef.current) {
      const message =
        totalCount === 0
          ? 'No attestations found'
          : totalCount === 1
            ? '1 attestation found'
            : `${totalCount} attestations found`

      announce(message, {assertive: true})
      lastQueryRef.current = currentQuery
    }
  }, [totalCount, currentQuery])

  const [selectedAttestationIds, setSelectedAttestationIds] = useState<Set<string>>(() => new Set())

  const defaultPageState = {
    currentPage: 1,
    pageCount: 1,
  }

  const [pageState] = useClientValue(
    () => {
      // Don't attempt to compute pagination if no attestations exist for this repo
      if (!pageInfo) {
        return defaultPageState
      }

      let currentPage = 1
      let pageCount = 1

      /*
       * Pagination logic: API only returns partial cursor information.
       * When paginating forward, only "hasNextPage" is reliable.
       * When paginating backward, only "hasPreviousPage" is reliable.
       */

      // Display "Next" button when either:
      // 1. API explicitly indicates more results (hasNextPage)
      // 2. We're already paginating backward (using 'before' param)
      if (currentPayload.pageInfo?.hasNextPage || params.get('before')) {
        pageCount += 1
      }

      // Display "Previous" button when either:
      // 1. API explicitly indicates previous results (hasPreviousPage)
      // 2. We're already paginating forward (using 'after' param)
      // Also update current page to 2 since we're not on the first page
      if (currentPayload.pageInfo?.hasPreviousPage || params.get('after')) {
        pageCount += 1
        currentPage = 2
      }

      return {
        currentPage,
        pageCount,
      }
    },
    defaultPageState,
    [params, pageInfo],
  )

  const filterProviders = useMemo(() => {
    return [new PredicateTypeFilterProvider(), new CreatedFilterProvider()]
  }, [])

  function getSortOrderFromQuery(query: string): SortOrderType {
    if (query.includes('sort:created-asc')) {
      return SortOrder.Oldest
    }
    return SortOrder.Newest
  }

  const currentSortOrder = useMemo(() => getSortOrderFromQuery(currentQuery), [currentQuery])

  function handleSortChange(newSortOrder: SortOrderType) {
    const sortString = newSortOrder === SortOrder.Oldest ? 'sort:created-asc' : 'sort:created-desc'
    const baseQuery = currentQuery.replace(/sort:created-(asc|desc)/g, '').trim()
    const newQuery = baseQuery ? `${baseQuery} ${sortString}` : sortString

    fetchResults(newQuery)
  }

  function handlePageChange(pageIndex: number) {
    if (!currentPayload.pageInfo) return

    const currentCursors = {
      startCursor: pageInfo.startCursor,
      endCursor: pageInfo.endCursor,
    }

    if (pageIndex > pageState.currentPage) {
      changePage('next', currentCursors, currentQuery)
    } else if (pageIndex < pageState.currentPage) {
      changePage('previous', currentCursors, currentQuery)
    }
  }

  return (
    <PageLayout rowGap="none">
      <PageLayout.Header>
        <Pagehead className={styles.Pagehead}>
          <div className={styles.Box}>
            <div className={styles.Box_1}>
              <Heading as="h2" className={styles.Heading}>
                Attestations
              </Heading>
            </div>
            {feedbackUrl?.length > 0 && (
              <div>
                <Link href={feedbackUrl} target="_blank" className="f6">
                  Give feedback
                </Link>
              </div>
            )}
          </div>
        </Pagehead>
      </PageLayout.Header>
      <PageLayout.Content as="div">
        <Filter
          id="attestations-index-filter"
          className="tmp-mb-3"
          label="Filter"
          onSubmit={filterQuery => fetchResults(filterQuery.raw)}
          providers={filterProviders}
        />
        <AttestationsList
          repo={repo}
          attestations={attestations}
          totalCount={totalCount || 0}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          currentSort={currentSortOrder}
          pageCount={pageState.pageCount}
          currentPage={pageState.currentPage}
          selectedAttestationIds={selectedAttestationIds.size > 0 ? selectedAttestationIds : new Set<string>()}
          onAttestationSelectionChange={setSelectedAttestationIds}
          onDelete={deleteAttestations}
          hasWritePermission={initialPayload.hasWritePermission}
        />
      </PageLayout.Content>
    </PageLayout>
  )
}
